/**
 * Spec 77 — Side-effect module that registers the 'workflow' panel type.
 * Spec 79 M1.5 D6 — Adds 4 viewer panel types:
 *   search-results, bundle-preview, team-proposal, artifact-preview.
 *
 * Imported once at app entry (main.tsx) to guarantee registration runs before
 * any conversation page mounts. The `render` function uses React.lazy so the
 * WorkflowBuilder chunk is only loaded when the panel actually opens — not at
 * bootstrap time. This also keeps the module free of DOM dependencies, which
 * lets the registry-bootstrap test run in a Node environment.
 *
 * generateCloseSummary calls the server's GET /workflows/:id/close-summary
 * endpoint (Spec 77 §6.3) when the panel closes; the framework then posts the
 * markdown summary to the parent conversation as a system card. Returns null
 * for non-conversation-sourced workflows (server signals via summary === null)
 * or on fetch failure — never blocks the panel close.
 */
import React from 'react';
import { getWorkflowCloseSummary } from '../../generated/api/sdk.gen.js';
import { unwrapGenerated } from '../../services/api/generated.js';
import { registerPanelType } from './registry.js';

const WorkflowBuilderPanelLazy = React.lazy(() =>
  import('./WorkflowBuilderPanel.js').then((m) => ({ default: m.WorkflowBuilderPanel })),
);

async function fetchWorkflowCloseSummary(id: string): Promise<string | null> {
  try {
    const res = await unwrapGenerated(getWorkflowCloseSummary({ path: { id } }));
    return res.summary;
  } catch {
    // Echo failures must never block the panel close. Swallow and return null.
    return null;
  }
}

registerPanelType({
  type: 'workflow',
  mode: 'editor',
  entryPoint: 'workflow-builder',
  // A conversation's workflow card opens it here too (owner 2026-10-31: a record opens inside the conversation).
  label: 'Workflow',
  policy: 'CanViewAgents',
  detailHref: (id) => `/agents/workflows/${id}`,
  render: ({ id, onClose }) =>
    React.createElement(
      React.Suspense,
      { fallback: null },
      React.createElement(WorkflowBuilderPanelLazy, { id, onClose }),
    ),
  generateCloseSummary: fetchWorkflowCloseSummary,
});

// ─── Spec 79 M1.5 D6 — viewer panel types ─────────────────────────────────────
// All four are mode='viewer': read-only, no assistant thread swap.
// Each component is lazy-loaded to keep the conversation page entry chunk lean.

const SearchResultsPanelLazy = React.lazy(() =>
  import('./panels/SearchResultsPanel.js').then((m) => ({ default: m.SearchResultsPanel })),
);

registerPanelType({
  type: 'search-results',
  mode: 'viewer',
  render: ({ id, onClose }) =>
    React.createElement(
      React.Suspense,
      { fallback: null },
      React.createElement(SearchResultsPanelLazy, { id, onClose }),
    ),
});

const BundlePreviewPanelLazy = React.lazy(() =>
  import('./panels/BundlePreviewPanel.js').then((m) => ({ default: m.BundlePreviewPanel })),
);

registerPanelType({
  type: 'bundle-preview',
  mode: 'viewer',
  render: ({ id, onClose }) =>
    React.createElement(
      React.Suspense,
      { fallback: null },
      React.createElement(BundlePreviewPanelLazy, { id, onClose }),
    ),
});

const TeamProposalPanelLazy = React.lazy(() =>
  import('./panels/TeamProposalPanel.js').then((m) => ({ default: m.TeamProposalPanel })),
);

registerPanelType({
  type: 'team-proposal',
  mode: 'viewer',
  render: ({ id, onClose }) =>
    React.createElement(
      React.Suspense,
      { fallback: null },
      React.createElement(TeamProposalPanelLazy, { id, onClose }),
    ),
});

const ArtifactPreviewPanelLazy = React.lazy(() =>
  import('./panels/ArtifactPreviewPanel.js').then((m) => ({ default: m.ArtifactPreviewPanel })),
);

registerPanelType({
  type: 'artifact-preview',
  mode: 'viewer',
  render: ({ id, onClose }) =>
    React.createElement(
      React.Suspense,
      { fallback: null },
      React.createElement(ArtifactPreviewPanelLazy, { id, onClose }),
    ),
});
